import React, { useState, useEffect } from "react";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

function People() {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [data, setData] = useState([]);
  const [dataOther, setDataOther] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filteredDataOther, setFilteredDataOther] = useState([]);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const commission = {
    Mihret: 150,
    Yeabkal: 150,
    Yeabtsega: 0,
  };

  const commissionForReceiver = {
    Mihret: 70,
    Yeabkal: 70,
    Yeabtsega: 70,
  };

  const deliveryCommissionRate = {
    Ephrem: 25,
    Fu: 40,
    Kidi: 30,
    Su: 40,
  };

  // Get the current month in short form
  const currentMonthShortForm = () => {
    const monthIndex = new Date().getMonth();
    const monthNames = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    return monthNames[monthIndex];
  };
  const [selectedMonth, setSelectedMonth] = useState(currentMonthShortForm()); // Initialize to current month

  const monthToNumber = (month) => {
    const months = {
      JAN: 1,
      FEB: 2,
      MAR: 3,
      APR: 4,
      MAY: 5,
      JUN: 6,
      JUL: 7,
      AUG: 8,
      SEP: 9,
      OCT: 10,
      NOV: 11,
      DEC: 12,
    };
    return months[month] || 0;
  };

  const parseMonthFromOrderNumber = (orderNumber) => {
    const monthPart = orderNumber.startsWith("OP")
      ? orderNumber.slice(2, 5)
      : orderNumber.slice(0, 3);
    return monthPart.toUpperCase();
  };

  const sortByOrderNumber = (data) => {
    return data.sort((a, b) => {
      const [aMonth, aNumber] = [
        parseMonthFromOrderNumber(a.OrderNumber),
        parseInt(a.OrderNumber.slice(-2)),
      ];
      const [bMonth, bNumber] = [
        parseMonthFromOrderNumber(b.OrderNumber),
        parseInt(b.OrderNumber.slice(-2)),
      ];
      const aMonthNum = monthToNumber(aMonth);
      const bMonthNum = monthToNumber(bMonth);
      return aMonthNum !== bMonthNum
        ? bMonthNum - aMonthNum
        : bNumber - aNumber;
    });
  };

  const filterDataByMonth = (data, month, year) => {
    return data.filter((item) => {
      if (!item.salesDate) return false;

      const date = new Date(item.TimeStamp);

      const itemMonth = parseMonthFromOrderNumber(item.OrderNumber);
      const itemYear = date.getFullYear();

      return itemMonth === month && itemYear === year;
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/items`);
      const fetchedData = response.data.map((item) => ({
        ...item,
        imageUrl1: `${apiUrl}/uploads/${item.ImageFilename1}`,
        imageUrl2: `${apiUrl}/uploads/${item.ImageFilename2}`,
      }));
      const sortedData = sortByOrderNumber(fetchedData);
      setData(sortedData);
      setFilteredData(
        filterDataByMonth(sortedData, selectedMonth, selectedYear)
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrors(error.response?.data?.message || "An error occurred");
    }
  };

  const fetchDataOther = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/otherItems`);
      const fetchedData = response.data.map((item) => ({
        ...item,
        imageUrl1: `${apiUrl}/uploads/${item.ImageFilename1}`,
        imageUrl2: `${apiUrl}/uploads/${item.ImageFilename2}`,
      }));
      const sortedData = sortByOrderNumber(fetchedData);
      setDataOther(sortedData);
      setFilteredDataOther(
        filterDataByMonth(sortedData, selectedMonth, selectedYear)
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrors(error.response?.data?.message || "An error occurred");
    }
  };

  useEffect(() => {
    fetchData();
    fetchDataOther();
  }, []);

  const handleMonthChange = (event) => {
    const month = event.target.value;
    setSelectedMonth(month);

    setFilteredData(filterDataByMonth(data, month, selectedYear));
    setFilteredDataOther(filterDataByMonth(dataOther, month, selectedYear));
  };

  const handleYearChange = (event) => {
    const year = Number(event.target.value);
    setSelectedYear(year);

    setFilteredData(filterDataByMonth(data, selectedMonth, year));
    setFilteredDataOther(filterDataByMonth(dataOther, selectedMonth, year));
  };

  const isSameMonth = (soldDate, selectedMonth, selectedYear) => {
    if (!soldDate) return false;
    const sold = new Date(soldDate);

    return (
      sold.getMonth() + 1 === monthToNumber(selectedMonth) &&
      sold.getFullYear() === selectedYear
    );
  };

  const isAfterMonth = (soldDate, selectedMonth, selectedYear) => {
    if (!soldDate) return false;

    const sold = new Date(soldDate);
    const selected = new Date(
      selectedYear,
      monthToNumber(selectedMonth) - 1,
      1
    );

    return sold > selected;
  };

  // Count items received per receiver in filteredData
  const countReceivers = (data) => {
    return data.reduce((acc, item) => {
      const receiver = item.receiver || "Unknown";
      acc[receiver] = (acc[receiver] || 0) + 1; // Count items by receiver name
      return acc;
    }, {});
  };

  // Count items delivered per deliveryPerson in filteredData
  const countByDeliveryPerson = (data) => {
    return data.reduce((acc, item) => {
      const deliveryPerson = item.deliveryPerson || "Unknown";
      acc[deliveryPerson] = (acc[deliveryPerson] || 0) + 1;
      return acc;
    }, {});
  };

  const countBySellerAndTiming = (data, selectedMonth) => {
    const onTime = {};
    const delayed = {};

    data.forEach((item) => {
      const seller = item.seller || "Unknown";
      if (isSameMonth(item.salesDate, selectedMonth, selectedYear)) {
        onTime[seller] = (onTime[seller] || 0) + 1;
      } else if (isAfterMonth(item.salesDate, selectedMonth)) {
        delayed[seller] = (delayed[seller] || 0) + 1;
      }
    });

    return { onTime, delayed };
  };

  const calculateCommissions = (sellerCounts) => {
    const counts = {
      Mihret: 0,
      Yeabkal: 0,
    };

    const commissions = {
      Mihret: 0,
      Yeabkal: 0,
    };

    const mihretCount = sellerCounts["Mihret"] || 0;
    const yeabkalCount = sellerCounts["Yeabkal"] || 0;
    const yeabtsegaCount = sellerCounts["Yeabtsega"] || 0;

    // Commissions
    commissions.Mihret = 150 * mihretCount;
    commissions.Yeabkal = 150 * yeabkalCount;
    // Counts
    counts.Mihret = mihretCount;
    counts.Yeabkal = yeabkalCount;
    counts.Yeabtsega = yeabtsegaCount;

    return { commissions, counts };
  };
  const deliveryPersonCounts = countByDeliveryPerson(filteredData);

  const receiverOnTime = countReceivers(filteredData);
  const { onTime: sellerOnTime, delayed: sellerDelayed } =
    countBySellerAndTiming(filteredData, selectedMonth);
  const sellerCommission = calculateCommissions(sellerOnTime, sellerDelayed);
  const { commissions: allCommissions, counts: allCounts } =
    calculateCommissions(sellerOnTime);

  return (
    <div className="items-center flex flex-col align-center justify-center mt-5 overflow-auto">
      <div className=" border-1 border-blue-500 mt-20 w-3/4 mb-20 p-5 rounded-md overflow-auto">
        <div className="w-3/4 mb-5">
          <label htmlFor="month-select" className="font-bold mr-2">
            Select Month:
          </label>
          <select
            id="month-select"
            onChange={handleMonthChange}
            value={selectedMonth}
          >
            <option value="">All</option>
            <option value="JAN">January</option>
            <option value="FEB">February</option>
            <option value="MAR">March</option>
            <option value="APR">April</option>
            <option value="MAY">May</option>
            <option value="JUN">June</option>
            <option value="JUL">July</option>
            <option value="AUG">August</option>
            <option value="SEP">September</option>
            <option value="OCT">October</option>
            <option value="NOV">November</option>
            <option value="DEC">December</option>
          </select>
          <select value={selectedYear} onChange={handleYearChange}>
            <option value={currentYear}>{currentYear}</option>
            <option value={currentYear - 1}>{currentYear - 1}</option>
            <option value={currentYear - 2}>{currentYear - 2}</option>
          </select>
        </div>

        <h2 className="text-xl font-bold mt-8 mb-2">
          Receivers ({selectedMonth})
        </h2>
        <table className="table-auto w-full text-left mb-6">
          <thead>
            <tr>
              <th className="bg-blue-100">Receiver</th>
              <th className="bg-blue-200">Count</th>
              <th className="bg-blue-100">Commission/pcs</th>
              <th className="bg-blue-100">Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(receiverOnTime).map(([name, count]) => (
              <tr key={name}>
                <td className="bg-blue-50">{name}</td>
                <td className="bg-blue-100">{count}</td>
                <td className="bg-blue-100">70</td>
                <td className="bg-blue-50">
                  {commissionForReceiver[name]
                    ? count * commissionForReceiver[name]
                    : 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2 className="text-xl font-bold mt-8 mb-2">
          Sellers – On-Time Sales {selectedMonth}
        </h2>
        <table className="table-auto w-full mb-10 text-left">
          <thead>
            <tr className="border-b-1">
              <th className="bg-blue-100">Name</th>
              <th className="bg-blue-200">Count</th>
              <th className="bg-blue-100">Commission/pcs</th>
              <th className="bg-blue-100">Total</th>
            </tr>
          </thead>
          <tbody>
            {["Mihret", "Yeabkal", "Yeabtsega"].map((name) => (
              <tr key={name}>
                <td className="bg-blue-50">{name}</td>
                <td className="bg-blue-100">{allCounts[name]}</td>
                <td className="bg-violet-50">
                  {commission[name] ? commission[name] : 0}
                </td>
                <td className="bg-blue-50">{allCommissions[name]}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="text-xl font-bold mt-8 mb-2">
          Sellers – Delayed Sales(After {selectedMonth})
        </h2>
        <table className="table-auto w-full mb-10 text-left">
          <thead>
            <tr className="border-b-1">
              <th className="bg-violet-100">Receiver</th>
              <th className="bg-violet-200">Count</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(sellerDelayed).map(([name, count]) => (
              <tr key={name}>
                <td className="bg-violet-50">{name}</td>
                <td className="bg-violet-100">{count}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="text-xl font-bold mt-8 mb-2">
          Shipment Commission ({selectedMonth})
        </h2>
        <table className="table-auto w-full text-left mb-6">
          <thead>
            <tr>
              <th className="bg-blue-100">Sender</th>
              <th className="bg-blue-200">Count</th>
              <th className="bg-blue-100">Commission/pcs</th>
              <th className="bg-blue-100">Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(deliveryPersonCounts).map(([name, count]) => (
              <tr key={name}>
                <td className="bg-blue-50">{name}</td>
                <td className="bg-blue-100">{count}</td>
                <td className="bg-blue-50">
                  {deliveryCommissionRate[name]
                    ? deliveryCommissionRate[name]
                    : 0}
                </td>
                <td className="bg-blue-50">
                  {deliveryCommissionRate[name]
                    ? count * deliveryCommissionRate[name]
                    : 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default People;
